import { AttachmentFile, collectRevisionAttachments } from 'utils/revision-attachments';
import { RevisionGetResponse } from 'api/actions/revision-get/revision-get-response';
import { RevisionFormData } from 'components/forms/revision/fill-out/types';
import { useMemo, useState } from 'react';
import { Box, Menu, Divider, Portal, Stack, Group } from '@mantine/core';
import FileThumbnail from 'components/files/FileThumbnail';
import Slider from 'components/modals/slider/Slider';
import P2Regular from 'components/typography/P2Regular';
import P1Medium from 'components/typography/P1Medium';
import { IconDotsVertical, IconDownload } from '@tabler/icons-react';
import useFileDownload from 'api/use-file-download';
import FileLink from 'components/files/FileLink';
import H3Medium from 'components/typography/H3Medium';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import ActionIcon from 'components/ActionIcon';
import ReadonlyText from 'components/typography/ReadonlyText';

interface RevisionAttachmentsProps {
  revisionData: RevisionFormData;
  revision: RevisionGetResponse;
}

interface AttachmentRowProps {
  file: AttachmentFile;
  onOpenSlider: (fileId: string) => void;
}

/**
 * Grid layout style for the attachments table.
 */
const gridStyle = {
  display: 'grid',
  gridTemplateColumns: '80px 3fr 3fr 3fr 40px',
  gap: '24px',
  borderRadius: '4px',
  alignItems: 'center',
};

/**
 * Single attachment row component.
 */
function AttachmentRow({ file, onOpenSlider }: AttachmentRowProps) {
  const { isMobile } = useResponsiveDesign();
  const { download } = useFileDownload();

  if (isMobile) {
    return (
      <Box py={16} px={16} sx={(theme) => ({ borderBottom: `1px solid ${theme.colors.gray[1]}` })}>
        <Stack spacing={12}>
          <Box w={44}>
            <FileThumbnail fileId={file.fileId} showSlider={true} onOpenSlider={() => onOpenSlider(file.fileId)} />
          </Box>
          <ReadonlyText
            title="Názov súboru"
            value={<FileLink fileId={file.fileId} onOpenSlider={() => onOpenSlider(file.fileId)} />}
          />
          <ReadonlyText title="Zariadenie" value={file.device ?? ''} />
          <ReadonlyText title="Umiestnenie" value={file.location} />
          <Menu position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'} aria-label="Akcie prílohy">
                <IconDotsVertical stroke="1.5" />
              </ActionIcon>
            </Menu.Target>
            <Portal>
              <Menu.Dropdown>
                <Menu.Item
                  color="gray.8"
                  icon={<IconDownload stroke={1.5} size={20} />}
                  onClick={() => download(file.fileId)}
                >
                  Stiahnúť
                </Menu.Item>
              </Menu.Dropdown>
            </Portal>
          </Menu>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={gridStyle} py={16} px={16}>
        {/* Thumbnail */}
        <Box w={44}>
          <FileThumbnail fileId={file.fileId} showSlider={true} onOpenSlider={() => onOpenSlider(file.fileId)} />
        </Box>

        {/* File name */}
        <FileLink fileId={file.fileId} onOpenSlider={() => onOpenSlider(file.fileId)} />

        {/* Device */}
        {file.device ? (
          <P2Regular color="gray.7">{file.device}</P2Regular>
        ) : (
          <Box data-testid={`empty-revision-attachment-${file.fileId}-device`} />
        )}

        {/* Location */}
        {file.location ? (
          <P2Regular color="gray.7">{file.location}</P2Regular>
        ) : (
          <Box data-testid={`empty-revision-attachment-${file.fileId}-location`} />
        )}

        {/* Actions menu */}
        <Menu position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'} aria-label="Akcie prílohy">
              <IconDotsVertical stroke="1.5" />
            </ActionIcon>
          </Menu.Target>
          <Portal>
            <Menu.Dropdown>
              <Menu.Item
                color="gray.8"
                icon={<IconDownload stroke={1.5} size={20} />}
                onClick={() => download(file.fileId)}
              >
                Stiahnúť
              </Menu.Item>
            </Menu.Dropdown>
          </Portal>
        </Menu>
      </Box>
      <Divider color="gray.1" />
    </Box>
  );
}

/**
 * Component to display revision attachments.
 */
export default function RevisionAttachments({ revisionData, revision }: RevisionAttachmentsProps) {
  const { isMobile, isTablet } = useResponsiveDesign();
  const [sliderOpened, setSliderOpened] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

  const attachments = useMemo(
    () => collectRevisionAttachments(revisionData, JSON.parse(revision.revisionTemplate?.formScheme ?? 'null')),
    [revisionData, revision.revisionTemplate]
  );

  const allFileIds = useMemo(() => attachments.map((file) => file.fileId), [attachments]);

  /**
   * Opens the slider with the selected file.
   */
  const handleOpenSlider = (fileId: string) => {
    setSelectedFileId(fileId);
    setSliderOpened(true);
  };

  return (
    <Stack spacing={24} pt={24} px={isTablet ? 20 : 40} pb={40} bg="white" style={{ borderRadius: '4px' }}>
      {/* Title */}
      <Group position="apart" spacing={16}>
        <H3Medium color="gray.7">Prílohy</H3Medium>
      </Group>

      {/* Header row */}
      {!isMobile && (
        <Box sx={gridStyle} bg="gray.0" p={16}>
          <P1Medium color="gray.8" pl={4}>
            Náhľad
          </P1Medium>
          <P1Medium color="gray.8">Názov súboru</P1Medium>
          <P1Medium color="gray.8">Zariadenie</P1Medium>
          <P1Medium color="gray.8">Umiestnenie</P1Medium>
          <Box />
        </Box>
      )}

      {/* Empty state */}
      {attachments.length === 0 && (
        <Box py={24} px={16}>
          <P1Medium>Momentálne nie sú nahraté žiadne prílohy</P1Medium>
        </Box>
      )}

      {/* Data rows */}
      <Stack spacing={0}>
        {attachments.map((file, index) => (
          <AttachmentRow key={`${file.fileId}-${index}`} file={file} onOpenSlider={handleOpenSlider} />
        ))}
      </Stack>

      {/* Slider for viewing images */}
      {selectedFileId && (
        <Slider
          opened={sliderOpened}
          fileIds={allFileIds}
          onClose={() => setSliderOpened(false)}
          initialImageId={selectedFileId}
        />
      )}
    </Stack>
  );
}
